import { Card, CardContent } from '@wedding-tech/ui/card'
import { Clock, Info } from 'lucide-react'
import { domAnimation, LazyMotion, m } from 'motion/react'
import { useEffect, useMemo, useState } from 'react'

import { useSettings } from '@/hooks/useSettings'
import { formatThaiDate } from '@/lib/date'
import { cn } from '@/lib/utils'

export const AgendaPage = () => {
  const { settings, isLoading } = useSettings()
  const s = settings as any

  const agendaList = useMemo(() => {
    return s?.agenda || []
  }, [s])

  const [currentId, setCurrentId] = useState<string | null>(null)

  // Real-time dynamic agenda active item logic
  useEffect(() => {
    const updateCurrent = () => {
      const now = new Date()
      // Use event date matching if configured
      if (s?.weddingDate) {
        const year = now.getFullYear()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const currentDateStr = `${year}-${month}-${day}`
        if (currentDateStr !== s.weddingDate) {
          setCurrentId(null)
          return
        }
      }

      const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
      let activeId: string | null = null

      for (let i = 0; i < agendaList.length; i++) {
        const item = agendaList[i]
        const nextItem = agendaList[i + 1]

        if (currentTimeStr >= item.time) {
          if (!nextItem || currentTimeStr < nextItem.time) {
            activeId = item.id
            break
          }
        }
      }

      setCurrentId(activeId)
    }

    updateCurrent()
    const interval = setInterval(updateCurrent, 60000)
    return () => clearInterval(interval)
  }, [agendaList, s?.weddingDate])

  useEffect(() => {
    if (currentId) {
      const element = document.getElementById(`agenda-${currentId}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [currentId])

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-stone-500">
        กำลังโหลดกำหนดการ...
      </div>
    )
  }

  const displayWeddingDate = s?.weddingDate ? formatThaiDate(s.weddingDate) : ''

  return (
    <LazyMotion features={domAnimation}>
      <div className="flex flex-1 flex-col">
        <main className="flex-1 space-y-8 p-6">
          {/* Intro Section */}
          <section className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="text-sm font-medium tracking-widest text-stone-500 uppercase">
                Wedding Schedule
              </p>
              {displayWeddingDate && (
                <h2 className="text-3xl leading-tight font-bold text-stone-900">
                  {displayWeddingDate}
                </h2>
              )}
              {agendaList.length > 0 && (
                <div className="flex items-center gap-2 text-stone-500">
                  <Info className="size-4" />
                  <p className="text-xs">
                    ลำดับพิธีการอาจมีการเปลี่ยนแปลงตามความเหมาะสม
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Timeline / Empty State */}
          {agendaList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-stone-500">
              <Clock className="mb-4 size-12 animate-pulse text-stone-300" />
              <h3 className="text-lg font-bold text-stone-700">
                ยังไม่มีกำหนดการ
              </h3>
              <p className="mt-1 text-sm text-stone-400">
                ผู้จัดงานยังไม่ได้ลงรายละเอียดกำหนดการในขณะนี้
              </p>
            </div>
          ) : (
            <section className="relative space-y-8 pb-12">
              {/* Vertical Line */}
              <div className="absolute top-2 bottom-2 left-[19px] w-0.5 bg-stone-200"></div>

              {agendaList.map((item: any, index: number) => {
                const isCurrent = item.id === currentId

                return (
                  <m.div
                    key={item.id}
                    id={`agenda-${item.id}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative flex items-start gap-6"
                  >
                    {/* Timeline Dot */}
                    <div
                      className={cn(
                        'relative z-10 mt-2 flex size-10 shrink-0 items-center justify-center rounded-full border-4 border-stone-50 transition-all duration-500',
                        isCurrent
                          ? 'bg-primary shadow-primary/20 scale-110 shadow-lg'
                          : 'bg-stone-200',
                      )}
                    >
                      <div
                        className={cn(
                          'size-2.5 rounded-full',
                          isCurrent ? 'bg-white' : 'bg-stone-400',
                        )}
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-2">
                        <div
                          className={cn(
                            'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold tracking-tight uppercase transition-colors',
                            isCurrent
                              ? 'bg-primary text-white'
                              : 'bg-stone-100 text-stone-500',
                          )}
                        >
                          <Clock className="size-3" />
                          {item.time} {item.endTime ? `- ${item.endTime}` : ''}{' '}
                          น.
                        </div>
                        {isCurrent && (
                          <m.span
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-primary flex items-center gap-1 text-[10px] font-bold uppercase"
                          >
                            <span className="bg-primary size-1.5 animate-pulse rounded-full" />
                            กำลังดำเนินอยู่
                          </m.span>
                        )}
                      </div>

                      <Card
                        className={cn(
                          'overflow-hidden border-none transition-all duration-300',
                          isCurrent
                            ? 'shadow-primary/5 ring-primary/5 scale-[1.02] bg-white shadow-xl ring-1'
                            : 'bg-white/60 opacity-80 shadow-sm',
                        )}
                      >
                        <CardContent className="p-5">
                          <h3
                            className={cn(
                              'text-lg font-bold transition-colors',
                              isCurrent ? 'text-primary' : 'text-stone-700',
                            )}
                          >
                            {item.title}
                          </h3>
                          {item.description && (
                            <p className="mt-1 text-sm leading-relaxed text-stone-500">
                              {item.description}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </m.div>
                )
              })}
            </section>
          )}
        </main>
      </div>
    </LazyMotion>
  )
}
